import {selectHexes, selectSpecialBuildings} from "../../../../redux/reducers/sharedStateReducer/selectors";
import {selectConstructionPlanEntries} from "../../../../redux/reducers/privateStateReducer/selectors";
import {TOKENS} from "../../../../constants/tokens";
import {checkIfSpecialBuilding} from "../../../buildings/checkIfSpecialBuilding";
import {calculateCitiesCache} from "../../calculateCitiesCache";
import {applyConstructionPlanEntriesToField} from "./buildConstructionPlanningViewState";
import {CONSTRUCTION_PLAN_CONTEXTS, getConstructionPlanContext} from "./getConstructionPlanContext";

const EMPTY_PLAN_ENTRIES = Object.freeze([]);

/**
 * План строительства, пока он НА ПОЛЕ: при планировании в чужой ход и в начале
 * своего, пока план не применен (например, ждет восстановления соединения), см.
 * getConstructionPlanContext. В остальное время записей на поле нет
 */
export const selectConstructionPlanEntriesOnField = (state) => (
  getConstructionPlanContext(state) === CONSTRUCTION_PLAN_CONTEXTS.NONE
    ? EMPTY_PLAN_ENTRIES
    : selectConstructionPlanEntries(state)
);

/**
 * Ложится ли запись плана поверх НАСТОЯЩЕГО содержимого ячейки. Если ходящий
 * игрок уже построил на ячейке, план поверх настоящего не рисуется: такое
 * запланированное снимается отменой (constructionPlanMiddleware), а до нее не
 * должно мелькать. Дорогу застроить можно — запланированное ложится поверх нее
 */
export const checkIfPlanEntryCoversHex = ({token}, realToken, realSpecialBuilding) => (
  checkIfSpecialBuilding(token)
    ? !realSpecialBuilding
    : (!realToken || realToken === TOKENS.ROAD)
);

// Кэш на одну запись: поле спрашивают на каждой перерисовке сцены по разу на
// ферму (см. calculateTokenImage), а его входы меняются заметно реже. Дороже
// всего в нем пересчет городов — полный обход поля
let cachedField = null;

/**
 * Поле таким, каким оно НАРИСОВАНО: настоящие жетоны и спецстроения и — пока
 * план строительства на поле — запланированные поверх них (правила те же, что
 * у отрисовки плана, см. renderPlannedBuildings). По нему считается все, что
 * игрок видит на поле «как будто запланированное уже построено»: картинка
 * фермы рядом с запланированным складом, сеть связей снабжения между фермами.
 *
 * Без плана на поле — сами hexes и specialBuildings стора, а citiesCache — null:
 * города настоящего поля каждый берет по-своему (кэш стора или свежий расчет).
 *
 * @returns {{hexes: Object, specialBuildings: Object, citiesCache: Object|null}}
 *   citiesCache — города поля с планом: запланированные кварталы, фермы и
 *   спецстроения в них уже входят
 */
export const selectFieldWithConstructionPlan = (state) => {
  const hexes = selectHexes(state);
  const specialBuildings = selectSpecialBuildings(state);
  const entries = selectConstructionPlanEntriesOnField(state);
  if (entries.length === 0) {
    return {hexes, specialBuildings, citiesCache: null};
  }

  if (cachedField
    && cachedField.hexes === hexes
    && cachedField.specialBuildings === specialBuildings
    && cachedField.entries === entries
  ) {
    return cachedField.field;
  }

  const planField = applyConstructionPlanEntriesToField(
    hexes,
    specialBuildings,
    entries.filter((entry) => checkIfPlanEntryCoversHex(entry, hexes[entry.hexKey], specialBuildings[entry.hexKey]))
  );
  const field = {
    ...planField,
    citiesCache: calculateCitiesCache(planField.hexes, planField.specialBuildings),
  };

  cachedField = {hexes, specialBuildings, entries, field};
  return field;
};
